import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import HttpBackend, { HttpBackendOptions, RequestCallback } from 'i18next-http-backend';
import LanguageDetector from 'i18next-browser-languagedetector';

/**
 * i18n Configuration
 *
 * Internationalization setup for multilingual support
 * - Loads translations from backend API
 * - Auto-detects user language (localStorage → browser)
 * - Supports 5 languages: en, de, fr, es, pl
 * - Namespaces: common, auth, posts, calendar, channels, settings, ai, dashboard, agency, errors, validation, admin
 */

export const SUPPORTED_LANGUAGES = ['en', 'de', 'fr', 'es', 'pl'] as const;
export const DEFAULT_LANGUAGE = 'en';

export type SupportedLanguage = typeof SUPPORTED_LANGUAGES[number];

/**
 * Type guard to check if a string is a supported language
 */
export const isSupportedLanguage = (lang: string): lang is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.includes(lang as SupportedLanguage);
};

i18n
  .use(HttpBackend) // Load translations from API
  .use(LanguageDetector) // Auto-detect user language
  .use(initReactI18next) // React integration
  .init({
    // Bounded retry for failed namespace loads (read by i18next top-level; backoff doubles: 0.5s, 1s, 2s)
    maxRetries: 3,
    retryTimeout: 500,
    fallbackLng: DEFAULT_LANGUAGE,
    supportedLngs: [...SUPPORTED_LANGUAGES],
    debug: import.meta.env.DEV,

    interpolation: {
      escapeValue: false, // React already escapes values
    },

    backend: {
      // Build id in the URL = immutable long-cache per deploy; new deploy = new URLs
      loadPath: `/api/i18n/{{lng}}/{{ns}}?v=${__I18N_BUILD_ID__}`,
      // Custom request (no customHeaders/requestOptions/saveMissing support); 429 is reported as 503 so http-backend retries it
      request: (_options: HttpBackendOptions, url: string, _payload: object | string, callback: RequestCallback) => {
        fetch(url, { credentials: 'same-origin' })
          .then(async (res) => {
            if (res.ok) {
              callback(null, { status: res.status, data: await res.text() });
              return;
            }
            callback(res.statusText || 'Error', { status: res.status === 429 ? 503 : res.status, data: '' });
          })
          .catch((err: Error) => callback(err, { status: 0, data: '' }));
      },
      // Parse the backend response (expects { translations: {...} } or direct object)
      parse: (data: string) => {
        const parsed = JSON.parse(data);
        return parsed.translations || parsed;
      },
      // Add request headers if needed
      requestOptions: {
        mode: 'cors',
        credentials: 'same-origin',
        cache: 'default',
      },
    },

    detection: {
      order: ['localStorage', 'navigator'], // Check localStorage first, then browser language
      caches: ['localStorage'], // Cache language preference in localStorage
      lookupLocalStorage: 'preferredLanguage',
    },

    ns: [
      'common',
      'auth',
      'posts',
      'calendar',
      'channels',
      'settings',
      'ai',
      'dashboard',
      'agency',
      'errors',
      'validation',
      'admin',
      'analytics',
    ], // Available namespaces
    defaultNS: 'common', // Default namespace for t() calls
    fallbackNS: 'common', // Shared keys (save, cancel, etc.) always accessible
  });

// Keep <html lang> in sync with the active language (a11y, SEO, browser translation, hyphenation)
const syncHtmlLang = (lng: string | undefined): void => {
  if (typeof document === 'undefined') return;
  const base = (lng || DEFAULT_LANGUAGE).split('-')[0];
  document.documentElement.lang = isSupportedLanguage(base) ? base : DEFAULT_LANGUAGE;
};
i18n.on('languageChanged', syncHtmlLang);
syncHtmlLang(i18n.resolvedLanguage || i18n.language);

export default i18n;
